import { useEffect, useRef, useState } from 'react';
import type { Gesto, IntentoDeGesto } from '@validate/core';
import { pedirReto, SesionNoDisponible } from './api.js';
import { mensajeDelFallo, useCamara } from './camara.js';
import { INSTRUCCION, useDeteccionDeGesto, type EstadoDeGesto } from './gestos.js';
import { Boton, Firma, Marco, Pantalla, Pasos, Pastilla } from './ui.js';

/**
 * Paso 2 de 3, segunda mitad: la prueba de vida activa. Los frames "Pruebas de identidad"
 * del Figma (1/4 … 4/4).
 *
 * ── UNA SOLA SESIÓN DE CÁMARA: EL «FRENTE» ES LA FOTO DEL ROSTRO ───────────────
 *
 * La pantalla de Rostro salió del flujo. Encendía la cámara una segunda vez para pedir
 * exactamente lo que el gesto 'frente' —que siempre abre la secuencia— ya consigue: la cara
 * centrada, mirando a cámara, con el detector confirmándolo. Así que el selfie se captura
 * AQUÍ, en el instante en que 'frente' se da por bueno, a resolución completa — el cotejo
 * contra el documento necesita píxeles, no los 320 px de la ráfaga. Una pantalla menos para
 * la persona, una luz de cámara que se enciende una sola vez.
 *
 * ── FASE DE GRABACIÓN: PRIMERO LA IMAGEN, LUEGO EL RETO ────────────────────────
 *
 * Probado en teléfono: la etapa facial pasaba en ~3 s y la ráfaga juntaba ~30 fotogramas —
 * el rPPG del servidor necesita ~5-6 s de cara quieta para encontrar el latido, y con menos
 * señal el pulso sale no medible y el caso cae a revisión humana. Por eso, tras confirmar
 * 'frente' (y capturar el selfie), se abre una fase de grabación explícita ANTES de avanzar
 * al siguiente gesto sorteado: el pulso se mide en esos segundos de quietud, y capturar
 * primero la imagen y LUEGO el reto es exactamente lo que le da tiempo al rPPG.
 *
 * ── LA SECUENCIA LA DICTA EL SERVIDOR, NO EL FIGMA ─────────────────────────────
 *
 * Los cuatro frames del diseño enseñan CÓMO se ve un gesto pedido (título, instrucción
 * subrayada, insignia n/4, óvalo). No enseñan CUÁLES: eso lo sortea el servidor en cada
 * reto, y esta pantalla pinta lo que llegue en `secuencia`, en su orden. Cablear aquí los
 * textos del Figma como secuencia fija sería anular el sorteo desde el cliente.
 *
 * ── PEDIR EL RETO ES CARO Y VA CON GUARDA ──────────────────────────────────────
 *
 * `pedirReto` GASTA un intento de tres (razonado en el servidor). El `useRef` no es manía:
 * en desarrollo, StrictMode monta-desmonta-monta los efectos, y sin guarda cada arranque de
 * esta pantalla quemaría DOS intentos. La persona tiene tres. Dos arranques y estaría fuera
 * sin haber hecho nada.
 *
 * ── AQUÍ TAMBIÉN SE CAPTURA LA RÁFAGA PARA EL SERVIDOR (#79) ───────────────────
 *
 * Mientras la persona hace los gestos, su cara está delante de la cámara varios segundos —
 * exactamente lo que /pulse necesita para buscar el latido y /prnu para la huella del
 * sensor. Se captura una ventana RODANTE de los últimos ~6 s a 10 fps, reducida a 320 px:
 * el pulso se mide en el color de la piel, no en 4K, y 60 fotogramas pequeños viajan en un
 * POST; sesenta de 1080p no. Del stream CRUDO, sin espejo — el espejo es cosmética del
 * `<video>`, y unos fotogramas espejados romperían el cotejo con el documento no, pero sí
 * la coherencia con lo que cualquier otra pieza asuma sobre la imagen.
 *
 * ── ESTE COMPONENTE NO ENVÍA: DEVUELVE ─────────────────────────────────────────
 *
 * Al completar los gestos llama a `onCompletado(intentos, selfie, fotogramas, fps)` y quien
 * lo montó lo desmonta. El envío vive en `App`, y el motivo es físico: mientras este
 * componente viva, la cámara está encendida. La pantalla de VERIFICANDO no debe convivir con
 * una luz de cámara prendida — desmontar este componente es lo que la apaga (ver `useCamara`).
 */

/** Cuánto se espera al motor de detección antes de dar la cámara por no preparada. */
const MS_DE_PACIENCIA_CON_EL_MOTOR = 6000;

/** La ráfaga: 10 fps, ventana rodante de 60 (~los últimos 6 s), 320 px de ancho. */
const MS_ENTRE_FOTOGRAMAS = 100;
const FOTOGRAMAS_TOPE = 60;
const ANCHO_DE_FOTOGRAMA = 320;

/**
 * Cuántos fotogramas junta la fase de grabación antes de soltar el siguiente gesto
 * (≈5,5 s a 10 fps): el rPPG necesita ~5 s de cara quieta; menos fotogramas = pulso no
 * medible = revisión humana.
 */
const FOTOGRAMAS_PARA_EL_PULSO = 55;

/**
 * Las instrucciones de colocación, en palabras de PERSONA: hablan del teléfono y del
 * cuerpo, no de coordenadas. 'sube' llega del detector cuando la cara sale pegada al borde
 * inferior — la corrección es subir el teléfono (ver gestos.ts sobre la dirección).
 */
function textoDeColocacion(colocacion: EstadoDeGesto['colocacion']): string {
  switch (colocacion) {
    case 'sin_rostro':
      return 'Buscando tu rostro…';
    case 'sube':
      return 'Sube un poco el teléfono';
    case 'baja':
      return 'Baja un poco el teléfono';
    case 'centra':
      return 'Centra tu rostro en el óvalo';
    case 'acerca':
      return 'Acércate un poco';
    case 'aleja':
      return 'Aléjate un poco';
    case 'ok':
      // Solo alcanzable en el instante raro de "colocación ok pero rostro aún no
      // publicado". Buscar es lo más honesto que se puede decir ahí.
      return 'Buscando tu rostro…';
  }
}

export function Gestos({
  token,
  onCompletado,
  onNoDisponible,
  onVolver,
}: {
  token: string;
  onCompletado: (intentos: IntentoDeGesto[], selfie: string, fotogramas: string[], fps: number) => void;
  /** La sesión ya no da más de sí (caducó, se quedó sin intentos). El mensaje es para la persona. */
  onNoDisponible: (mensaje: string) => void;
  onVolver?: () => void;
}) {
  const [secuencia, setSecuencia] = useState<Gesto[] | null>(null);
  const [indice, setIndice] = useState(0);
  const [seAgotoLaPaciencia, setSeAgotoLaPaciencia] = useState(false);
  // La fase de grabación (ver la cabecera): entre 'frente' y el siguiente gesto, cara
  // quieta mientras la ráfaga junta señal suficiente para el rPPG.
  const [grabando, setGrabando] = useState(false);
  const [fotogramasJuntados, setFotogramasJuntados] = useState(0);

  const intentos = useRef<IntentoDeGesto[]>([]);
  const fotogramas = useRef<string[]>([]);
  const selfie = useRef<string | null>(null);
  const pedido = useRef(false);
  // Espejos en ref para los intervalos de la ráfaga y de la grabación: leen el último
  // valor sin figurar en dependencias — rearmar el interval de captura en cada tick del
  // detector desalinearía la cadencia y perdería fotogramas.
  const posicionOk = useRef(false);
  const grabandoRef = useRef(false);
  grabandoRef.current = grabando;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const { stream, fallo, reintentar } = useCamara('rostro');

  useEffect(() => {
    if (pedido.current) return;
    pedido.current = true;

    pedirReto(token)
      .then((r) => setSecuencia(r.secuencia))
      .catch((e: unknown) => {
        onNoDisponible(
          e instanceof SesionNoDisponible
            ? e.message
            : 'No pudimos preparar la prueba. Revisa tu conexión y vuelve a abrir el enlace.',
        );
      });
    // `onNoDisponible` fuera de las dependencias a propósito: cambia de identidad en cada
    // render de App y este efecto NO debe rearmarse — la guarda lo haría inofensivo, pero
    // mejor no dar la vuelta siquiera.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const v = videoRef.current;
    if (v !== null && stream !== null) {
      v.srcObject = stream;
      void v.play().catch(() => undefined);
      setVideo(v);
    }
  }, [stream, secuencia]);

  // La ráfaga para el servidor. Corre en paralelo a la detección, con su propio lienzo.
  useEffect(() => {
    if (video === null || secuencia === null || fallo !== null) return;

    const lienzo = document.createElement('canvas');
    const t = setInterval(() => {
      if (video.videoWidth === 0) return;
      // Durante la grabación, un fotograma sin la cara bien puesta NO entra en la ráfaga:
      // para el rPPG es basura que diluye la señal (la piel que busca no está donde debe,
      // o no está). Fuera de la grabación se empuja como siempre — la ventana rodante ya
      // se encarga de quedarse con lo último.
      if (grabandoRef.current && !posicionOk.current) return;
      const alto = Math.round((video.videoHeight / video.videoWidth) * ANCHO_DE_FOTOGRAMA);
      lienzo.width = ANCHO_DE_FOTOGRAMA;
      lienzo.height = alto;
      const ctx = lienzo.getContext('2d');
      if (ctx === null) return;
      ctx.drawImage(video, 0, 0, ANCHO_DE_FOTOGRAMA, alto);
      fotogramas.current.push(lienzo.toDataURL('image/jpeg', 0.7));
      // Rodante: se quedan los ÚLTIMOS. Al final de los gestos la cara lleva un rato quieta
      // y centrada — es la mejor ventana para medir, no la primera.
      if (fotogramas.current.length > FOTOGRAMAS_TOPE) fotogramas.current.shift();
    }, MS_ENTRE_FOTOGRAMAS);

    return () => clearInterval(t);
  }, [video, secuencia, fallo]);

  // La fase de grabación: cada 300 ms se mira cuánto lleva juntado la ráfaga y, al llegar
  // al umbral del pulso, se suelta el siguiente gesto. No captura nada por sí misma — la
  // ráfaga de arriba sigue corriendo; esto solo RETIENE el avance hasta que haya señal.
  useEffect(() => {
    if (!grabando) return;
    const t = setInterval(() => {
      // El progreso solo avanza con la cara bien puesta: lo que se juntara sin ella sería
      // basura para el rPPG (y de hecho la ráfaga ya no lo captura). Si la cara se va, el
      // conteo se CONGELA — no se resetea: castigar el temblor de una mano con volver a
      // cero sería crueldad gratis.
      if (!posicionOk.current) return;
      const juntados = fotogramas.current.length;
      setFotogramasJuntados(juntados);
      if (juntados >= FOTOGRAMAS_PARA_EL_PULSO) {
        clearInterval(t);
        setGrabando(false);
        setIndice(1);
      }
    }, 300);
    return () => clearInterval(t);
  }, [grabando]);

  const gestoActual = secuencia?.[indice];

  const estado = useDeteccionDeGesto(
    video,
    gestoActual ?? 'frente',
    // Activa TAMBIÉN durante la grabación: el lazo ya no confirma gestos tras disparar
    // (ver gestos.ts) pero sigue publicando colocación y luz — es lo que permite congelar
    // la ráfaga y avisar si la cara se sale del encuadre en plena fase quieta.
    gestoActual !== undefined && fallo === null,
    (ms) => {
      if (gestoActual === undefined || secuencia === null) return;
      // El 'frente' ES la foto del rostro (ver la cabecera): en el instante en que el
      // detector lo confirma, la cara está centrada y mirando a cámara — mejor momento no
      // hay. A resolución completa, porque el cotejo contra el documento necesita píxeles;
      // la ráfaga de 320 px es para el pulso, no para comparar caras.
      if (gestoActual === 'frente' && selfie.current === null && video !== null && video.videoWidth > 0) {
        const lienzo = document.createElement('canvas');
        lienzo.width = video.videoWidth;
        lienzo.height = video.videoHeight;
        const ctx = lienzo.getContext('2d');
        if (ctx !== null) {
          ctx.drawImage(video, 0, 0);
          selfie.current = lienzo.toDataURL('image/jpeg', 0.92);
        }
      }
      intentos.current.push({ gesto: gestoActual, ms, afirmadoPorElCliente: true });
      if (gestoActual === 'frente' && indice === 0) {
        // NO se avanza todavía: aquí arranca la fase de grabación (ver la cabecera). La
        // cara ya está centrada y quieta — es el momento de dejar que la ráfaga junte los
        // segundos que el rPPG necesita, ANTES de pedirle a la persona que se mueva.
        setGrabando(true);
        return;
      }
      if (indice + 1 >= secuencia.length) {
        // El fallback al último fotograma de la ráfaga existe por si el selfie no llegó a
        // capturarse (un contexto 2D nulo, un vídeo sin dimensiones en ese instante): un
        // fotograma pequeño es peor que el selfie, pero infinitamente mejor que nada. El ''
        // final jamás debería ocurrir — la secuencia siempre empieza en 'frente' y para
        // cuando se completa el último gesto la ráfaga lleva segundos llenándose.
        onCompletado(
          intentos.current,
          selfie.current ?? [...fotogramas.current].pop() ?? '',
          [...fotogramas.current],
          1000 / MS_ENTRE_FOTOGRAMAS,
        );
      } else {
        setIndice(indice + 1);
      }
    },
  );

  // ¿La cara está bien puesta AHORA MISMO? Lo leen los intervalos vía ref (sin rearmarse)
  // y la pantalla vía const — misma verdad, un solo cálculo por render.
  const posicionOkAhora = estado.hayRostro && estado.colocacion === 'ok';
  posicionOk.current = posicionOkAhora;

  useEffect(() => {
    if (estado.motorCargado) return;
    const t = setTimeout(() => setSeAgotoLaPaciencia(true), MS_DE_PACIENCIA_CON_EL_MOTOR);
    return () => clearTimeout(t);
  }, [estado.motorCargado]);

  if (fallo !== null) {
    const m = mensajeDelFallo(fallo);
    return (
      <Pantalla>
        <Pasos paso={2} onVolver={onVolver} />
        <div className="flex flex-1 flex-col justify-center gap-3 px-6">
          <h1 className="text-[24px] font-bold leading-tight">{m.titulo}</h1>
          <p className="text-[14px] leading-relaxed" style={{ color: 'rgba(16,17,20,.6)' }}>
            {m.queHacer}
          </p>
          {fallo !== 'denegado' && fallo !== 'sin_https' ? (
            <div className="mt-3">
              <Boton onClick={reintentar}>Intentar otra vez</Boton>
            </div>
          ) : null}
        </div>
        <Firma />
      </Pantalla>
    );
  }

  if (secuencia === null) {
    // El reto viene en camino. Fondo oscuro y una frase: en un buen 4G esto dura menos de lo
    // que tarda en leerse.
    return (
      <div
        className="mx-auto flex min-h-[100dvh] w-full max-w-[440px] flex-col"
        style={{ background: '#101114', fontFamily: 'var(--tipografia, Inter, system-ui, sans-serif)' }}
      >
        <Pasos paso={2} onVolver={onVolver} sobreVideo />
        <div className="flex flex-1 items-center justify-center">
          <p className="text-[14px]" style={{ color: 'rgba(255,255,255,.8)' }}>
            Preparando la prueba…
          </p>
        </div>
      </div>
    );
  }

  const motorMuerto = seAgotoLaPaciencia && !estado.motorCargado;

  return (
    <div
      className="relative mx-auto flex min-h-[100dvh] w-full max-w-[440px] flex-col overflow-hidden"
      style={{ background: '#101114', fontFamily: 'var(--tipografia, Inter, system-ui, sans-serif)' }}
    >
      {/* Espejo en el VÍDEO, nunca en la detección, la ráfaga ni el selfie: ver arriba. */}
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        className="absolute inset-0 h-full w-full object-cover"
        style={{ transform: 'scaleX(-1)' }}
      />
      <div className="absolute inset-0" style={{ background: 'rgba(16,17,20,.38)' }} aria-hidden />

      <div className="relative flex flex-1 flex-col">
        <Pasos paso={2} onVolver={onVolver} sobreVideo />

        <div className="px-6 pt-5 text-center text-white">
          <h1 className="text-[23px] font-bold">Pruebas de identidad</h1>
          {/* La instrucción subrayada del Figma: "1.Centra tu rostro en el marco."
              Durante la grabación no hay gesto que pedir: la instrucción es quedarse quieto.
              Y ANTES de exigir el primer gesto, ayudar a encuadrar: la queja real del campo
              fue una cara al borde inferior con una pantalla que no decía qué hacer. */}
          <p
            className="mx-auto mt-1.5 inline-block border-b border-white/70 pb-0.5 text-[13px] font-medium"
            aria-live="polite"
          >
            {grabando
              ? 'Mantén tu rostro en el óvalo, no te muevas…'
              : indice === 0 && estado.colocacion !== 'ok'
                ? 'Ponte frente a la cámara, dentro del óvalo.'
                : `${indice + 1}. ${INSTRUCCION[secuencia[indice] ?? 'frente']}`}
          </p>
        </div>

        {/* La insignia n/4 del Figma, sobre el óvalo. Durante la grabación, el punto rojo
            de toda la vida: no es un paso más de la cuenta, es una captura en curso. */}
        <div className="flex justify-center pt-4">
          <span
            className="grid h-10 w-10 place-items-center rounded-full bg-white text-[13px] font-bold"
            style={{ color: 'var(--acento, #3332f2)' }}
          >
            {grabando ? '●' : `${indice + 1}/${secuencia.length}`}
          </span>
        </div>

        <div className="flex flex-1 items-center">
          <div className="w-full">
            {/* Durante la grabación el aro enseña cuánta señal lleva juntada la ráfaga —
                ver crecer el progreso es lo que sostiene los ~5 s de quietud sin que la
                persona crea que la pantalla se colgó. */}
            <Marco
              progreso={grabando ? fotogramasJuntados / FOTOGRAMAS_PARA_EL_PULSO : indice / secuencia.length}
              forma="rostro"
            />
          </div>
        </div>

        <div className="flex justify-center gap-2 pb-3">
          <Pastilla texto="Luz" ok={estado.luzOk} />
          <Pastilla texto="Nitidez" ok={estado.nitidezOk} />
        </div>

        {motorMuerto ? (
          <div className="px-6 pb-8">
            <div
              className="rounded-2xl px-4 py-3 text-center text-[13px] leading-relaxed"
              style={{ background: 'rgba(255,255,255,.95)', color: '#101114' }}
              role="alert"
            >
              No pudimos preparar la cámara para la prueba. Revisa tu conexión y vuelve a
              abrir el enlace.
            </div>
          </div>
        ) : (
          // Por prioridad: en grabación manda la colocación (si está mal, decir CÓMO
          // arreglarla; si está bien, pedir quietud); fuera de ella, la guía de colocación
          // solo aplica al gesto 'frente' — en los giros el centro se mueve adrede y
          // corregirlo sería contradecir la propia instrucción del gesto.
          <p
            className="pb-8 text-center text-[12.5px] font-medium"
            style={{ color: 'rgba(255,255,255,.9)', minHeight: '1.2em' }}
            aria-live="polite"
          >
            {grabando
              ? posicionOkAhora
                ? 'Capturando tu rostro… no te muevas'
                : textoDeColocacion(estado.colocacion)
              : gestoActual === 'frente' && estado.colocacion !== 'ok'
                ? textoDeColocacion(estado.colocacion)
                : estado.hayRostro
                  ? 'Detección automática…'
                  : 'Buscando tu rostro…'}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * La prueba no cuadró y el servidor lo dijo, O el envío no llegó y hay que reintentarlo.
 * El mensaje que se enseña es para la persona: qué hacer, nunca por qué falló.
 */
export function PruebaNoCompletada({
  mensaje,
  onReintentar,
}: {
  mensaje: string;
  onReintentar: () => void;
}) {
  return (
    <Pantalla>
      <Pasos paso={2} />
      <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
        <h1 className="text-[24px] font-bold leading-tight">No pudimos completar la prueba</h1>
        <p className="mt-2 max-w-[300px] text-[14px] leading-relaxed" style={{ color: 'rgba(16,17,20,.6)' }}>
          {mensaje}
        </p>
        <div className="mt-7 w-full">
          {/* Reintentar la PRUEBA pide otro reto y eso gasta un intento — el servidor lleva
              la cuenta. Cuando no queden, pedirReto fallará y la pantalla de "enlace no
              disponible" tomará el relevo. La cuenta no se enseña: "te quedan 2 intentos" es
              presión gratis para el honesto e información gratis para el otro. */}
          <Boton onClick={onReintentar}>Intentar de nuevo</Boton>
        </div>
      </div>
      <Firma />
    </Pantalla>
  );
}
